import React, { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  addDoc,
} from "firebase/firestore";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import "./adminusers.css";

const IRISH_COUNTIES = [
  "Antrim", "Armagh", "Carlow", "Cavan", "Clare", "Cork", "Derry", "Donegal",
  "Down", "Dublin", "Fermanagh", "Galway", "Kerry", "Kildare", "Kilkenny",
  "Laois", "Leitrim", "Limerick", "Longford", "Louth", "Mayo", "Meath",
  "Monaghan", "Offaly", "Roscommon", "Sligo", "Tipperary", "Tyrone",
  "Waterford", "Westmeath", "Wexford", "Wicklow",
];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  // Form data for adding/editing users
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "user",
    allowedCounties: [],
  });

  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setCurrentAdmin(user);
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    fetchUsers();
  }, []);

  async function fetchUsers() {
    try {
      setLoading(true);
      const snap = await getDocs(collection(db, "users"));
      setUsers(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    } catch (err) {
      console.error("Error fetching users:", err);
      showMessage("Error loading users", "error");
    } finally {
      setLoading(false);
    }
  }

  function showMessage(text, type = "success") {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 5000);
  }

  function resetForm() {
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "user",
      allowedCounties: [],
    });
  }

  function handleCountyToggle(county) {
    setFormData((prev) => ({
      ...prev,
      allowedCounties: prev.allowedCounties.includes(county)
        ? prev.allowedCounties.filter((c) => c !== county)
        : [...prev.allowedCounties, county],
    }));
  }

  function selectAllCounties() {
    setFormData((prev) => ({
      ...prev,
      allowedCounties: [...IRISH_COUNTIES],
    }));
  }

  function clearAllCounties() {
    setFormData((prev) => ({
      ...prev,
      allowedCounties: [],
    }));
  }

  async function handleAddUser(e) {
    e.preventDefault();

    if (formData.allowedCounties.length === 0 && formData.role === "editor") {
      showMessage("Please select at least one county for editors", "error");
      return;
    }

    try {
      setLoading(true);

      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Add user to Firestore
      await addDoc(collection(db, "users"), {
        uid: userCredential.user.uid,
        name: formData.name,
        email: formData.email,
        role: formData.role,
        allowedCounties: formData.role === "admin" ? [] : formData.allowedCounties,
        createdAt: new Date().toISOString(),
        emailVerified: true,
      });

      showMessage("User added successfully!", "success");
      setShowAddModal(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      console.error("Error adding user:", err);
      if (err.code === "auth/email-already-in-use") {
        showMessage("Email already in use", "error");
      } else {
        showMessage("Error adding user: " + err.message, "error");
      }
    } finally {
      setLoading(false);
    }
  }

  const deleteUser = async (userId) => {
    if (!window.confirm(
      "Are you sure you want to delete this user? This will permanently remove them from both the authentication system and database."
    )) return;

    const currentUser = auth.currentUser;
    if (!currentUser) {
      alert("You must be logged in to delete users. Please log in again.");
      navigate("/login");
      return;
    }

    setDeletingUserId(userId);
    try {
      const token = await currentUser.getIdToken(true);

      const response = await fetch(
        "https://us-central1-craic-on-the-case.cloudfunctions.net/deleteUserV2",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ userId }),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setUsers((prev) => prev.filter((u) => u.id !== userId));
        showMessage("User deleted successfully!", "success");
      } else {
        throw new Error(result.error || result.message || "Failed to delete user");
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      showMessage("Failed to delete user: " + err.message, "error");
    } finally {
      setDeletingUserId(null);
    }
  };

  const saveUserEdits = async () => {
    if (editingUser.allowedCounties.length === 0 && editingUser.role === "editor") {
      showMessage("Please select at least one county for editors", "error");
      return;
    }

    try {
      const ref = doc(db, "users", editingUser.id);

      await updateDoc(ref, {
        name: editingUser.name || "",
        email: editingUser.email || "",
        role: editingUser.role || "user",
        allowedCounties: editingUser.role === "admin" ? [] : (editingUser.allowedCounties || []),
      });

      setUsers((prev) =>
        prev.map((u) =>
          u.id === editingUser.id ? { ...editingUser } : u
        )
      );

      setEditingUser(null);
      showMessage("User updated successfully!", "success");
    } catch (err) {
      console.error("Error updating user:", err);
      showMessage("Failed to update user", "error");
    }
  };

  function openEditModal(user) {
    setEditingUser({
      ...user,
      name: user.name || "",
      email: user.email || "",
      role: user.role || "user",
      allowedCounties: user.allowedCounties || [],
    });
  }

  function handleEditCountyToggle(county) {
    setEditingUser((prev) => ({
      ...prev,
      allowedCounties: prev.allowedCounties.includes(county)
        ? prev.allowedCounties.filter((c) => c !== county)
        : [...prev.allowedCounties, county],
    }));
  }

  function selectAllCountiesEdit() {
    setEditingUser((prev) => ({
      ...prev,
      allowedCounties: [...IRISH_COUNTIES],
    }));
  }

  function clearAllCountiesEdit() {
    setEditingUser((prev) => ({
      ...prev,
      allowedCounties: [],
    }));
  }

  const filtered = users.filter((u) =>
    (u.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (u.email || "").toLowerCase().includes(search.toLowerCase())
  );

  if (loading && users.length === 0) {
    return (
      <div className="dash-page">
        <div className="dash-loader">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-page">
      <div className="dash-container">
        <header className="dash-header">
          <div className="dash-header-content">
            <h1 className="dash-title">User Management</h1>
            <p className="dash-subtitle">
              View, edit, and manage all users • Total: {users.length}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className="dash-btn primary"
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 1V15M1 8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Add User
            </button>
            <button 
              className="admin-badge" 
              onClick={() => navigate("/admin")}
              style={{ cursor: 'pointer' }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 2L2 6V14H6V10H10V14H14V6L8 2Z" fill="currentColor"/>
              </svg>
              Dashboard
            </button>
          </div>
        </header>

        {message.text && (
          <div className={`dash-message ${message.type}`}>
            <div className="message-icon">
              {message.type === "error" ? "✕" : "✓"}
            </div>
            <p>{message.text}</p>
          </div>
        )}

        {currentAdmin && (
          <div className="dash-user-info" style={{ marginBottom: '32px' }}>
            <div className="user-avatar">
              {currentAdmin.displayName?.charAt(0).toUpperCase() || currentAdmin.email?.charAt(0).toUpperCase() || "A"}
            </div>
            <div className="user-details">
              <h2>{currentAdmin.displayName || "Admin"}</h2>
              <p>{currentAdmin.email}</p>
            </div>
            <div className="verified-badge">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 0L12.5 7.5L20 10L12.5 12.5L10 20L7.5 12.5L0 10L7.5 7.5L10 0Z"
                  fill="currentColor"
                />
              </svg>
              Admin
            </div>
          </div>
        )}

        <div className="dash-card" style={{ marginBottom: '32px' }}>
          <div className="card-header">
            <h2>Search Users</h2>
            <p>Filter by name or email address</p>
          </div>
          <div className="form-group">
            <input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {search && (
            <p style={{ marginTop: '12px', color: 'var(--color-text-secondary)', fontSize: '14px' }}>
              Showing {filtered.length} of {users.length} users
            </p>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="dash-card">
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: '16px' }}>
                {search ? `No users found matching "${search}"` : "No users found"}
              </p>
            </div>
          </div>
        ) : (
          <div className="admin-users-grid">
            {filtered.map((user) => (
              <div key={user.id} className="user-card">
                <div className="user-card-header">
                  <div className="user-card-avatar">
                    {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || "U"}
                  </div>
                  <div className="user-card-info">
                    <h3>{user.name || "Unnamed User"}</h3>
                    <p>{user.email || "No email"}</p>
                  </div>
                  <div className={`role-badge ${user.role === 'admin' ? 'admin' : user.role === 'editor' ? 'editor' : 'user'}`}>
                    {user.role === 'admin' ? 'Admin' : user.role === 'editor' ? 'Editor' : 'User'}
                  </div>
                </div>

                <div className="user-card-meta">
                  <div className="meta-item">
                    <span className="meta-label">Role</span>
                    <span className="meta-value">
                      {user.role === 'admin' ? 'Administrator' : user.role === 'editor' ? 'Editor' : 'User'}
                    </span>
                  </div>
                  {user.role === 'editor' && (
                    <div className="meta-item">
                      <span className="meta-label">Counties</span>
                      <span className="meta-value">
                        {user.allowedCounties?.length > 0 
                          ? `${user.allowedCounties.length} assigned`
                          : 'None assigned'
                        }
                      </span>
                    </div>
                  )}
                  <div className="meta-item">
                    <span className="meta-label">Member since</span>
                    <span className="meta-value">
                      {user.createdAt?.toDate
                        ? user.createdAt.toDate().toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric"
                          })
                        : user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric"
                            })
                          : "Unknown"}
                    </span>
                  </div>
                </div>

                {user.role === 'editor' && user.allowedCounties?.length > 0 && (
                  <div className="county-tags">
                    {user.allowedCounties.slice(0, 3).map((county) => (
                      <span key={county} className="county-tag-small">
                        {county}
                      </span>
                    ))}
                    {user.allowedCounties.length > 3 && (
                      <span className="county-tag-small more">
                        +{user.allowedCounties.length - 3} more
                      </span>
                    )}
                  </div>
                )}

                <div className="user-card-actions">
                  <button
                    className="dash-btn secondary"
                    onClick={() => openEditModal(user)}
                    disabled={deletingUserId === user.id}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M11.5 1L14 3.5L5.5 12H3V9.5L11.5 1Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                    </svg>
                    Edit
                  </button>

                  <button
                    className="dash-btn danger"
                    onClick={() => deleteUser(user.id)}
                    disabled={deletingUserId === user.id}
                  >
                    {deletingUserId === user.id ? (
                      <>
                        <div className="btn-spinner"></div>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M3 4H13M5 4V3C5 2.44772 5.44772 2 6 2H10C10.5523 2 11 2.44772 11 3V4M6.5 7V11M9.5 7V11M4 4H12V13C12 13.5523 11.5523 14 11 14H5C4.44772 14 4 13.5523 4 13V4Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                        </svg>
                        Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add User Modal */}
        {showAddModal && (
          <div className="admin-modal">
            <div className="admin-modal-overlay" onClick={() => setShowAddModal(false)}></div>
            <div className="admin-modal-content">
              <div className="modal-header">
                <h2>Add New User</h2>
                <button 
                  className="modal-close"
                  onClick={() => setShowAddModal(false)}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleAddUser} className="modal-body">
                <div className="form-group">
                  <label htmlFor="add-name">Full Name</label>
                  <input
                    id="add-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="add-email">Email Address</label>
                  <input
                    id="add-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="user@example.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="add-password">Password</label>
                  <input
                    id="add-password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Minimum 6 characters"
                    required
                    minLength="6"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="add-role">Role</label>
                  <select
                    id="add-role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <option value="user">User</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                  <p className="form-help">
                    Users can browse trails. Editors can manage trails in assigned counties. Admins have full access.
                  </p>
                </div>

                {formData.role === "editor" && (
                  <div className="form-group">
                    <label>Allowed Counties</label>
                    <div className="county-actions">
                      <button type="button" className="county-action-btn" onClick={selectAllCounties}>
                        Select All
                      </button>
                      <button type="button" className="county-action-btn" onClick={clearAllCounties}>
                        Clear All
                      </button>
                    </div>
                    <div className="county-grid">
                      {IRISH_COUNTIES.map((county) => (
                        <label key={county} className="county-checkbox">
                          <input
                            type="checkbox"
                            checked={formData.allowedCounties.includes(county)}
                            onChange={() => handleCountyToggle(county)}
                          />
                          <span>{county}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="modal-footer">
                  <button 
                    type="button"
                    className="dash-btn secondary" 
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="dash-btn primary"
                  >
                    Add User
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {editingUser && (
          <div className="admin-modal">
            <div className="admin-modal-overlay" onClick={() => setEditingUser(null)}></div>
            <div className="admin-modal-content">
              <div className="modal-header">
                <h2>Edit User</h2>
                <button 
                  className="modal-close"
                  onClick={() => setEditingUser(null)}
                >
                  ✕
                </button>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="edit-name">Name</label>
                  <input
                    id="edit-name"
                    type="text"
                    value={editingUser.name}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, name: e.target.value })
                    }
                    placeholder="Enter name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-email">Email</label>
                  <input
                    id="edit-email"
                    type="email"
                    value={editingUser.email}
                    disabled
                    style={{ opacity: 0.6, cursor: 'not-allowed' }}
                  />
                  <p className="form-help">Email cannot be changed</p>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-role">Role</label>
                  <select
                    id="edit-role"
                    value={editingUser.role}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, role: e.target.value })
                    }
                  >
                    <option value="user">User</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                  <p className="form-help">
                    Users can browse trails. Editors can manage trails in assigned counties. Admins have full access.
                  </p>
                </div>

                {editingUser.role === "editor" && (
                  <div className="form-group">
                    <label>Allowed Counties</label>
                    <div className="county-actions">
                      <button type="button" className="county-action-btn" onClick={selectAllCountiesEdit}>
                        Select All
                      </button>
                      <button type="button" className="county-action-btn" onClick={clearAllCountiesEdit}>
                        Clear All
                      </button>
                    </div>
                    <div className="county-grid">
                      {IRISH_COUNTIES.map((county) => (
                        <label key={county} className="county-checkbox">
                          <input
                            type="checkbox"
                            checked={editingUser.allowedCounties?.includes(county) || false}
                            onChange={() => handleEditCountyToggle(county)}
                          />
                          <span>{county}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button 
                  className="dash-btn secondary" 
                  onClick={() => setEditingUser(null)}
                >
                  Cancel
                </button>
                <button 
                  className="dash-btn primary" 
                  onClick={saveUserEdits}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}